/**
 * ElegantTemplate.tsx
 * ────────────────────────────────────────────────────────────────────────
 * Template sofisticado de InnovaAgent. Plan mínimo: starter.
 * Paleta gold/champagne sobre slate oscuro, tipografía serif, animaciones
 * lentas (500-800ms). Target: residencial premium.
 *
 * Identidad dual separada:
 *   - PERSONAL (avatar + name + prefix) → hero, bio.
 *   - CORPORATIVA (logo + business_name) → header, footer.
 *
 * Checklist de 7 puntos:
 *   [x] 1. AgentFull desde _shared/types
 *   [x] 2. Flags show_* vía helpers
 *   [x] 3. <a siempre envuelto en contenedor (nunca primer token tras &&)
 *   [x] 4. Footer con docs legales + cache + modal
 *   [x] 5. Disclaimers NOM-247 en cards
 *   [x] 6. Sello NOM-247 (useNomSeal)
 *   [x] 7. SEO + Schema.org (setPageSEO)
 *
 * Refactorizado: 20 Abril 2026
 * ────────────────────────────────────────────────────────────────────────
 */

import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  Shield,
  Bed,
  Bath,
  Toilet,
  Maximize,
  ArrowUpRight,
  Menu,
  X,
  Facebook,
  Instagram,
  Linkedin,
  Youtube,
  Home,
  Building,
  Building2,
  Key,
  Briefcase,
  Star,
  ChevronDown,
} from 'lucide-react'

import ChatWidget from '@/components/ChatWidget'
import PropertyFilters, {
  FILTERS_INITIAL_STATE,
  PropertyFiltersState,
  applyFilters,
} from '@/components/PropertyFilters'
import PropertyPagination from '@/components/PropertyPagination'

import type {
  AgentTemplateProps,
  Property,
  LegalDocType,
} from './_shared/types'
import {
  getSubdomain,
  formatPrice,
  resolveContactChannels,
  resolveIdentityDisplay,
  resolveImageStrategy,
  resolveLegalDocs,
  resolveChatConfig,
  useNomSeal,
} from './_shared/agentHelpers'
import { setPageSEO } from './_shared/seoHelpers'
import { API_ROUTES } from './_shared/constants'

// ══════════════════════════════════════════════════════════════════════════
// i18n-ready — todos los literales aquí
// ══════════════════════════════════════════════════════════════════════════
const content = {
  kicker_default: 'Asesor Inmobiliario',
  kicker_with_brand: (brand: string) => `${brand} · Asesor Inmobiliario`,
  hero_scroll_hint: 'Ver el portafolio',
  hero_cta_call: 'Llamar',
  hero_cta_whatsapp: 'WhatsApp',
  hero_cta_email: 'Escribir',
  hero_cta_visit: 'Agendar visita',
  hero_chat_fallback:
    'Conversemos. El chat en esta página está disponible para atenderte personalmente.',
  hero_chat_fallback_no_ia:
    'Contáctame a través de mis canales oficiales.',
  nav_properties: 'Propiedades',
  nav_about: 'El asesor',
  nav_contact: 'Contacto',
  nav_menu: 'Menú',
  nav_close: 'Cerrar',
  about_heading: 'El asesor detrás de cada operación',
  about_kicker: 'Perfil profesional',
  properties_heading: 'Portafolio seleccionado',
  properties_kicker: 'Propiedades',
  properties_empty: 'Próximamente publicaré nuevas propiedades.',
  properties_count_singular: 'propiedad',
  properties_count_plural: 'propiedades',
  property_cta: 'Ver detalle',
  disclaimer_price: 'Precio sujeto a cambios sin previo aviso',
  disclaimer_images: 'Las imágenes pueden ser ilustrativas',
  construction_preventa: 'Preventa',
  construction_en_construccion: 'En construcción',
  construction_terminado: 'Terminado',
  purpose_venta: 'En venta',
  purpose_renta: 'En renta',
  purpose_preventa: 'Preventa',
  purpose_leasing: 'Leasing',
  specialties_heading: 'Especialidades',
  specialties_kicker: 'Áreas de expertise',
  zones_heading: 'Zonas de cobertura',
  zones_kicker: 'Territorio',
  cta_final_heading: 'El inicio de tu próxima operación inmobiliaria',
  cta_final_subheading:
    'Cada conversación es el inicio de un acompañamiento a la medida.',
  footer_heading_contact: 'Contacto',
  footer_heading_address: 'Domicilio',
  footer_heading_hours: 'Horario de atención',
  footer_heading_complaints: 'Atención a quejas',
  footer_heading_legal: 'Información legal',
  footer_heading_social: 'En redes',
  footer_copyright: (brand: string) =>
    `© ${new Date().getFullYear()} ${brand}. Todos los derechos reservados.`,
  legal_modal_title: 'Documento legal',
  legal_modal_close: 'Cerrar',
  legal_aviso_privacidad: 'Aviso de Privacidad',
  legal_terminos: 'Términos y Condiciones',
  legal_derechos_consumidor: 'Derechos del Consumidor',
  legal_info_nom247: 'Información NOM-247',
  na: '—',
}

// ══════════════════════════════════════════════════════════════════════════
// Helpers de etiquetado (puros, fuera del componente)
// ══════════════════════════════════════════════════════════════════════════

const legalDocLabel = (docType: LegalDocType): string => {
  switch (docType) {
    case 'aviso_privacidad':
      return content.legal_aviso_privacidad
    case 'terminos_condiciones':
      return content.legal_terminos
    case 'derechos_consumidor':
      return content.legal_derechos_consumidor
    case 'info_nom247':
      return content.legal_info_nom247
  }
}

const constructionLabel = (
  status: NonNullable<Property['construction_status']>
): string => {
  switch (status) {
    case 'preventa':
      return content.construction_preventa
    case 'en_construccion':
      return content.construction_en_construccion
    case 'terminado':
      return content.construction_terminado
  }
}

const purposeLabel = (purpose: string): string => {
  switch (purpose?.toLowerCase()) {
    case 'venta':
      return content.purpose_venta
    case 'renta':
      return content.purpose_renta
    case 'preventa':
      return content.purpose_preventa
    case 'leasing':
      return content.purpose_leasing
    default:
      return content.purpose_venta
  }
}

const specialtyIcon = (specialty: string) => {
  const lower = specialty.toLowerCase()
  if (lower.includes('casa')) return Home
  if (lower.includes('depa') || lower.includes('departamento')) return Building
  if (lower.includes('preventa')) return Star
  if (lower.includes('renta')) return Key
  if (lower.includes('leasing') || lower.includes('oficina')) return Briefcase
  return Building2
}

// SVG de WhatsApp minimal (lucide no tiene el oficial)
const WhatsAppIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
)

const TikTokIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
)

// ══════════════════════════════════════════════════════════════════════════
// Componente principal
// ══════════════════════════════════════════════════════════════════════════

const ElegantTemplate: React.FC<AgentTemplateProps> = ({ agent }) => {
  const navigate = useNavigate()

  // Helpers resueltos una sola vez
  const contact = resolveContactChannels(agent)
  const identity = resolveIdentityDisplay(agent)
  const images = resolveImageStrategy(agent)
  const legal = resolveLegalDocs(agent)
  const chat = resolveChatConfig(agent)
  const nomSeal = useNomSeal(agent)

  const brandForCopyright =
    identity.corporate.show && identity.corporate.name
      ? identity.corporate.name
      : identity.personal.show
        ? identity.personal.name
        : agent.name

  const heroKicker = identity.corporate.show
    ? content.kicker_with_brand(identity.corporate.name)
    : content.kicker_default

  // Estado
  const [properties, setProperties] = useState<Property[]>([])
  const [filters, setFilters] = useState<PropertyFiltersState>(FILTERS_INITIAL_STATE)
  const [showFilters, setShowFilters] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [perPage, setPerPage] = useState(6)
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const [legalModal, setLegalModal] = useState<{
    open: boolean
    type: LegalDocType | null
    html: string
  }>({ open: false, type: null, html: '' })
  const legalDocsCache = useRef<Record<string, string>>({})

  // Fetch propiedades
  useEffect(() => {
    const subdomain = getSubdomain()
    fetch(API_ROUTES.publicProperties(subdomain))
      .then((r) => (r.ok ? r.json() : []))
      .then((data: Property[]) =>
        setProperties(Array.isArray(data) ? data : [])
      )
      .catch(() => setProperties([]))
  }, [])

  // SEO
  useEffect(() => {
    setPageSEO(agent, {
      ogImage: images.personal.url ?? images.corporate.url ?? undefined,
    })
  }, [agent, images.personal.url, images.corporate.url])

  // Reset paginación al cambiar filtros
  useEffect(() => {
    setCurrentPage(1)
  }, [filters])

  // Derivados
  const filteredProperties = applyFilters(properties, filters)
  const propertyTypes = [
    ...new Set(properties.map((p) => p.property_type).filter(Boolean)),
  ]
  const totalPages =
    perPage === 0 ? 1 : Math.ceil(filteredProperties.length / perPage)
  const paginatedProperties =
    perPage === 0
      ? filteredProperties
      : filteredProperties.slice(
          (currentPage - 1) * perPage,
          currentPage * perPage
        )

  const social = agent.social_media ?? {}

  // Handlers
  const handlePropertyClick = (seoTitle: string) => {
    navigate(`/property/${seoTitle}`)
  }

  const openLegalDoc = async (docType: LegalDocType) => {
    if (legalDocsCache.current[docType]) {
      setLegalModal({
        open: true,
        type: docType,
        html: legalDocsCache.current[docType],
      })
      return
    }
    try {
      const subdomain = getSubdomain()
      const res = await fetch(API_ROUTES.legalDocPreview(docType, subdomain))
      const data = await res.json()
      legalDocsCache.current[docType] = data.html
      setLegalModal({ open: true, type: docType, html: data.html })
    } catch {
      // silencio consciente
    }
  }

  const closeLegalModal = () =>
    setLegalModal({ open: false, type: null, html: '' })

  // Escenario de imágenes para decidir layout del hero
  const heroHasPersonalImage = images.personal.show && !!images.personal.url

  // ════════════════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════════════════
  return (
    <div className="relative min-h-screen bg-slate-950 text-slate-100 font-sans antialiased selection:bg-amber-400/30 selection:text-amber-100">

      {/* ══ SELLO NOM-247 (top-right, fijo) ══ */}
      {nomSeal.visible && (
        <div className="fixed top-3 right-3 sm:top-5 sm:right-5 md:top-6 md:right-6 z-[60]">
          <a 
            href={nomSeal.verifyUrl!}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={nomSeal.copy.ariaLabel}
            title={nomSeal.copy.tooltip}
            className="block group"
          >
            <img
              src={nomSeal.assetPath}
              alt={nomSeal.copy.altText}
              className="h-11 sm:h-14 lg:h-16 xl:h-20 w-auto drop-shadow-[0_4px_18px_rgba(0,0,0,0.45)] group-hover:scale-105 transition-transform duration-500 ease-out"
            />
            <span className="sr-only">{nomSeal.copy.linkText}</span>
          </a>
        </div>
      )}

      {/* ══ HEADER CORPORATIVO ══ */}
      <header className="absolute top-0 left-0 right-0 z-30">
        <nav className="container mx-auto px-5 sm:px-8 py-5 sm:py-7 flex items-center justify-between gap-4">
          {/* Marca corporativa */}
          <div className="flex items-center gap-3 sm:gap-4 min-w-0">
            {images.corporate.show && images.corporate.url ? (
              <img
                src={images.corporate.url}
                alt={identity.corporate.name || brandForCopyright}
                className="h-9 sm:h-10 md:h-12 w-auto object-contain"
              />
            ) : identity.corporate.show ? (
              <span className="font-serif text-base sm:text-lg md:text-xl tracking-[0.18em] uppercase text-amber-200/90 truncate">
                {identity.corporate.name}
              </span>
            ) : (
              <span className="font-serif text-base sm:text-lg md:text-xl tracking-[0.18em] uppercase text-amber-200/90 truncate">
                {identity.personal.name}
              </span>
            )}
          </div>

          {/* Nav desktop */}
          <ul className="hidden md:flex items-center gap-10 text-xs tracking-[0.28em] uppercase text-slate-300">
            <li>
              <a href="#propiedades" className="relative py-1 hover:text-amber-300 transition-colors duration-300 after:content-[''] after:absolute after:left-0 after:right-0 after:-bottom-1 after:h-px after:bg-amber-400 after:scale-x-0 hover:after:scale-x-100 after:origin-left after:transition-transform after:duration-500">
                {content.nav_properties}
              </a>
            </li>
            <li>
              <a href="#sobre-el-asesor" className="relative py-1 hover:text-amber-300 transition-colors duration-300 after:content-[''] after:absolute after:left-0 after:right-0 after:-bottom-1 after:h-px after:bg-amber-400 after:scale-x-0 hover:after:scale-x-100 after:origin-left after:transition-transform after:duration-500">
                {content.nav_about}
              </a>
            </li>
            <li>
              <a href="#contacto" className="relative py-1 hover:text-amber-300 transition-colors duration-300 after:content-[''] after:absolute after:left-0 after:right-0 after:-bottom-1 after:h-px after:bg-amber-400 after:scale-x-0 hover:after:scale-x-100 after:origin-left after:transition-transform after:duration-500">
                {content.nav_contact}
              </a>
            </li>
          </ul>

          {/* Botón menú móvil */}
          <button
            type="button"
            onClick={() => setMobileNavOpen((v) => !v)}
            aria-label={mobileNavOpen ? content.nav_close : content.nav_menu}
            aria-expanded={mobileNavOpen}
            className="md:hidden text-slate-200 hover:text-amber-300 transition-colors p-2 -mr-2"
          >
            {mobileNavOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </nav>

        {/* Nav móvil desplegable */}
        {mobileNavOpen && (
          <div className="md:hidden bg-slate-950/98 backdrop-blur-md border-t border-slate-800/60">
            <ul className="container mx-auto px-5 py-6 flex flex-col gap-5 text-sm tracking-[0.22em] uppercase text-slate-200">
              <li>
                <a href="#propiedades" onClick={() => setMobileNavOpen(false)} className="block py-1 hover:text-amber-300 transition-colors">
                  {content.nav_properties}
                </a>
              </li>
              <li>
                <a href="#sobre-el-asesor" onClick={() => setMobileNavOpen(false)} className="block py-1 hover:text-amber-300 transition-colors">
                  {content.nav_about}
                </a>
              </li>
              <li>
                <a href="#contacto" onClick={() => setMobileNavOpen(false)} className="block py-1 hover:text-amber-300 transition-colors">
                  {content.nav_contact}
                </a>
              </li>
            </ul>
          </div>
        )}
      </header>

      {/* ══ HERO (identidad personal) ══ */}
      <section className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-zinc-950">
        {/* Watermark corporativo en el fondo (si hay logo) */}
        {images.corporate.show && images.corporate.url && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-[0.04]">
            <img
              src={images.corporate.url}
              alt=""
              aria-hidden="true"
              className="w-[70%] max-w-2xl blur-sm"
            />
          </div>
        )}

        {/* Rayo dorado decorativo */}
        <div className="absolute top-1/4 -right-40 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl pointer-events-none" aria-hidden="true" />
        <div className="absolute bottom-1/4 -left-40 w-96 h-96 bg-amber-400/5 rounded-full blur-3xl pointer-events-none" aria-hidden="true" />

        <div className="relative container mx-auto px-5 sm:px-8 pt-32 pb-20 md:pt-40 md:pb-28">
          <div className="grid md:grid-cols-12 gap-12 md:gap-16 items-center">

            {/* Columna de texto */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className={heroHasPersonalImage ? 'md:col-span-7' : 'md:col-span-12 text-center md:text-left max-w-4xl md:mx-auto'}
            >
              {/* Kicker */}
              <div className="flex items-center gap-3 mb-8 justify-start">
                <span className="h-px w-10 bg-amber-400/60" aria-hidden="true" />
                <span className="text-[11px] sm:text-xs tracking-[0.32em] uppercase text-amber-300/90 font-medium">
                  {heroKicker}
                </span>
              </div>

              {/* Título / identidad personal */}
              <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light leading-[1.05] tracking-tight text-white mb-8">
                {identity.personal.show ? identity.personal.displayName : identity.corporate.name || agent.name}
              </h1>

              {/* Filete decorativo */}
              <div className="h-px w-16 bg-gradient-to-r from-amber-400 to-transparent mb-8" aria-hidden="true" />

              {/* Bio */}
              {agent.bio && (
                <p className="text-base sm:text-lg md:text-xl text-slate-300 leading-relaxed font-light max-w-2xl mb-10">
                  {agent.bio}
                </p>
              )}

              {/* Zonas destacadas como metadata editorial */}
              {agent.zones && agent.zones.length > 0 && (
                <div className="flex flex-wrap items-center gap-x-6 gap-y-3 mb-12 text-xs sm:text-sm tracking-[0.18em] uppercase text-slate-400">
                  <span className="flex items-center gap-2 text-amber-300/80">
                    <MapPin size={14} aria-hidden="true" />
                    <span>{content.zones_kicker}</span>
                  </span>
                  {agent.zones.slice(0, 4).map((zone, i) => (
                    <span key={zone} className="flex items-center gap-3">
                      {i > 0 && <span className="w-1 h-1 rounded-full bg-amber-500/50" aria-hidden="true" />}
                      <span>{zone}</span>
                    </span>
                  ))}
                </div>
              )}

              {/* CTAs — cada <a> envuelto en contenedor */}
              {contact.hasAnyChannel ? (
                <div className="flex flex-wrap items-center gap-5 sm:gap-7">
                  {contact.showPhone && (
                    <div className="inline-flex">
                      <a
                        href={`tel:${contact.phoneDigits}`}
                        className="group inline-flex items-center gap-3 text-sm tracking-[0.22em] uppercase text-amber-300 border-b border-amber-400/60 hover:border-amber-300 pb-2 transition-colors duration-500"
                      >
                        <Phone size={16} aria-hidden="true" />
                        <span>{content.hero_cta_call}</span>
                        <ArrowUpRight size={16} className="opacity-0 -ml-1 group-hover:opacity-100 group-hover:ml-0 transition-all duration-500" aria-hidden="true" />
                      </a>
                    </div>
                  )}
                  {contact.showWhatsapp && (
                    <div className="inline-flex">
                      <a
                        href={`https://wa.me/${contact.whatsappDigits}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group inline-flex items-center gap-3 text-sm tracking-[0.22em] uppercase text-amber-300 border-b border-amber-400/60 hover:border-amber-300 pb-2 transition-colors duration-500"
                      >
                        <WhatsAppIcon className="w-4 h-4" />
                        <span>{content.hero_cta_whatsapp}</span>
                        <ArrowUpRight size={16} className="opacity-0 -ml-1 group-hover:opacity-100 group-hover:ml-0 transition-all duration-500" aria-hidden="true" />
                      </a>
                    </div>
                  )}
                  {contact.showEmail && (
                    <div className="inline-flex">
                      <a
                        href={`mailto:${contact.email}`}
                        className="group inline-flex items-center gap-3 text-sm tracking-[0.22em] uppercase text-slate-200 border-b border-slate-500/60 hover:border-amber-300 hover:text-amber-300 pb-2 transition-colors duration-500"
                      >
                        <Mail size={16} aria-hidden="true" />
                        <span>{content.hero_cta_email}</span>
                        <ArrowUpRight size={16} className="opacity-0 -ml-1 group-hover:opacity-100 group-hover:ml-0 transition-all duration-500" aria-hidden="true" />
                      </a>
                    </div>
                  )}
                </div>
              ) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 1, delay: 0.4 }}
                  className="max-w-xl"
                >
                  <p className="text-base sm:text-lg text-slate-300 font-light italic leading-relaxed border-l-2 border-amber-400/60 pl-5">
                    {chat.showIA
                      ? content.hero_chat_fallback
                      : content.hero_chat_fallback_no_ia}
                  </p>
                </motion.div>
              )}

              {/* Scroll hint */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 1.2 }}
                className="mt-16 flex items-center gap-3 text-[11px] tracking-[0.32em] uppercase text-slate-500"
              >
                <span>{content.hero_scroll_hint}</span>
                <ChevronDown size={14} className="animate-bounce" aria-hidden="true" />
              </motion.div>
            </motion.div>

            {/* Columna de imagen personal */}
            {heroHasPersonalImage && (
              <motion.div
                initial={{ opacity: 0, scale: 0.94 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
                className="md:col-span-5 relative mx-auto md:mx-0 w-full max-w-sm md:max-w-none"
              >
                <div className="relative aspect-[3/4] overflow-hidden">
                  {/* Marco dorado */}
                  <div className="absolute -top-3 -right-3 w-full h-full border border-amber-400/40 pointer-events-none" aria-hidden="true" />
                  <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 via-transparent to-transparent pointer-events-none z-10" aria-hidden="true" />
                  <img
                    src={images.personal.url!}
                    alt={identity.personal.displayName}
                    className="w-full h-full object-cover grayscale-[30%] hover:grayscale-0 transition-[filter] duration-1000"
                  />
                </div>
              </motion.div>
            )}

            {/* Fallback: sin avatar, mostrar iniciales tipográficas */}
            {!heroHasPersonalImage && identity.personal.show && images.personal.initialsFallback && (
              <motion.div
                initial={{ opacity: 0, scale: 0.94 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
                className="md:col-span-5 hidden md:flex items-center justify-center"
              >
                <div className="relative">
                  <div className="absolute -top-3 -right-3 w-64 h-64 border border-amber-400/40 pointer-events-none" aria-hidden="true" />
                  <div className="w-64 h-64 flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800">
                    <span className="font-serif text-8xl text-amber-300/80 tracking-tight">
                      {images.personal.initialsFallback}
                    </span>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </section>

      {/* ══ PROPIEDADES ══ */}
      <section id="propiedades" className="relative py-24 md:py-32 bg-slate-900">
        <div className="container mx-auto px-5 sm:px-8">
          {/* Encabezado editorial */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl mb-16 md:mb-20"
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-amber-400/60" aria-hidden="true" />
              <span className="text-[11px] tracking-[0.32em] uppercase text-amber-300/90">
                {content.properties_kicker}
              </span>
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-light text-white leading-tight">
              {content.properties_heading}
            </h2>
          </motion.div>

          {/* Filtros */}
          {properties.length > 0 && (
            <div className="mb-12">
              <PropertyFilters
                filters={filters}
                onFiltersChange={setFilters}
                propertyTypes={propertyTypes}
                totalProperties={properties.length}
                filteredCount={filteredProperties.length}
                colorSchemeKey="navy"
                showFilters={showFilters}
                onToggleFilters={() => setShowFilters((v) => !v)}
              />
            </div>
          )}

          {/* Grid de propiedades */}
          {paginatedProperties.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
              {paginatedProperties.map((property, index) => {
                const halfBaths = property.half_bathrooms ?? 0
                const areaNum =
                  property.area_m2 !== undefined && property.area_m2 !== null
                    ? Number(property.area_m2)
                    : null
                return (
                  <motion.article
                    key={property.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-50px' }}
                    transition={{ duration: 0.7, delay: Math.min(index * 0.08, 0.4) }}
                    className="group cursor-pointer bg-slate-950/40 border border-slate-800/60 hover:border-amber-400/40 transition-colors duration-500 flex flex-col"
                    onClick={() => handlePropertyClick(property.seo_title)}
                  >
                    {/* Imagen */}
                    <div className="relative aspect-[4/3] overflow-hidden bg-slate-900">
                      {property.featured_image ? (
                        <img
                          src={property.featured_image}
                          alt={property.title}
                          className="w-full h-full object-cover grayscale-[20%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-[900ms] ease-out"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-slate-700">
                          <Home size={48} aria-hidden="true" />
                        </div>
                      )}
                      {/* Badge de propósito */}
                      <div className="absolute top-4 left-4 bg-slate-950/80 backdrop-blur-sm border border-amber-400/30 px-3 py-1.5">
                        <span className="text-[10px] tracking-[0.24em] uppercase text-amber-300">
                          {purposeLabel(property.purpose)}
                        </span>
                      </div>
                      {/* Badge construction_status */}
                      {property.construction_status && (
                        <div className="absolute top-4 right-4 bg-amber-400/90 text-slate-950 px-3 py-1.5">
                          <span className="text-[10px] tracking-[0.2em] uppercase font-semibold">
                            {constructionLabel(property.construction_status)}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Contenido */}
                    <div className="flex-1 flex flex-col p-6 md:p-7">
                      {/* Precio */}
                      <div className="mb-4">
                        <p className="font-serif text-2xl md:text-3xl text-amber-300 font-light tracking-tight">
                          {formatPrice(property.price, property.currency)}
                        </p>
                      </div>

                      {/* Título */}
                      <h3 className="font-serif text-lg md:text-xl text-white font-light leading-snug mb-3 group-hover:text-amber-200 transition-colors duration-500 line-clamp-2">
                        {property.title}
                      </h3>

                      {/* Ubicación */}
                      <div className="flex items-center gap-2 text-xs tracking-[0.16em] uppercase text-slate-400 mb-5">
                        <MapPin size={13} aria-hidden="true" />
                        <span className="truncate">{property.zone}</span>
                      </div>

                      {/* Specs */}
                      <div className="flex items-center flex-wrap gap-x-5 gap-y-2 text-xs text-slate-300 mb-6 pb-6 border-b border-slate-800/60">
                        {property.bedrooms !== undefined && property.bedrooms !== null && property.bedrooms > 0 && (
                          <span className="flex items-center gap-1.5">
                            <Bed size={14} aria-hidden="true" />
                            <span>{property.bedrooms}</span>
                          </span>
                        )}
                        {property.bathrooms !== undefined && property.bathrooms !== null && property.bathrooms > 0 && (
                          <span className="flex items-center gap-1.5">
                            <Bath size={14} aria-hidden="true" />
                            <span>{property.bathrooms}</span>
                          </span>
                        )}
                        {halfBaths > 0 && (
                          <span className="flex items-center gap-1.5">
                            <Toilet size={14} aria-hidden="true" />
                            <span>{halfBaths}</span>
                          </span>
                        )}
                        {areaNum !== null && areaNum > 0 && (
                          <span className="flex items-center gap-1.5">
                            <Maximize size={14} aria-hidden="true" />
                            <span>{areaNum.toFixed(0)} m²</span>
                          </span>
                        )}
                      </div>

                      {/* Disclaimers NOM-247 */}
                      <div className="text-[10px] tracking-wide text-slate-500/90 space-y-1 mb-6">
                        <p>{content.disclaimer_price}</p>
                        <p>{content.disclaimer_images}</p>
                      </div>

                      {/* CTA ver detalle */}
                      <div className="mt-auto flex items-center justify-between text-xs tracking-[0.24em] uppercase text-amber-300/90 group-hover:text-amber-200 transition-colors duration-500">
                        <span>{content.property_cta}</span>
                        <ArrowUpRight size={16} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-500" aria-hidden="true" />
                      </div>
                    </div>
                  </motion.article>
                )
              })}
            </div>
          ) : (
            <div className="py-16 text-center">
              <p className="text-slate-400 font-serif italic text-lg">
                {content.properties_empty}
              </p>
            </div>
          )}

          {/* Paginación */}
          {properties.length > 0 && (
            <div className="mt-16">
              <PropertyPagination
                currentPage={currentPage}
                totalPages={totalPages}
                perPage={perPage}
                onPageChange={setCurrentPage}
                onPerPageChange={setPerPage}
                colorSchemeKey="navy"
              />
            </div>
          )}
        </div>
      </section>

      {/* ══ SOBRE EL ASESOR (identidad personal extendida) ══ */}
      {(identity.personal.show || agent.bio) && (
        <section id="sobre-el-asesor" className="relative py-24 md:py-32 bg-slate-950">
          <div className="container mx-auto px-5 sm:px-8">
            <div className="grid md:grid-cols-12 gap-12 md:gap-16 items-start max-w-6xl mx-auto">

              {/* Imagen */}
              {heroHasPersonalImage && (
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.9 }}
                  className="md:col-span-5"
                >
                  <div className="relative aspect-[3/4] overflow-hidden max-w-sm mx-auto md:mx-0">
                    <div className="absolute -bottom-3 -left-3 w-full h-full border border-amber-400/40 pointer-events-none" aria-hidden="true" />
                    <img
                      src={images.personal.url!}
                      alt={identity.personal.displayName}
                      className="relative w-full h-full object-cover grayscale hover:grayscale-0 transition-[filter] duration-1000"
                    />
                  </div>
                </motion.div>
              )}

              {/* Texto */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.9, delay: 0.15 }}
                className={heroHasPersonalImage ? 'md:col-span-7' : 'md:col-span-12 max-w-3xl'}
              >
                <div className="flex items-center gap-3 mb-6">
                  <span className="h-px w-10 bg-amber-400/60" aria-hidden="true" />
                  <span className="text-[11px] tracking-[0.32em] uppercase text-amber-300/90">
                    {content.about_kicker}
                  </span>
                </div>
                <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-light text-white leading-tight mb-8">
                  {content.about_heading}
                </h2>
                <div className="h-px w-16 bg-gradient-to-r from-amber-400 to-transparent mb-8" aria-hidden="true" />

                {agent.bio && (
                  <p className="text-base md:text-lg text-slate-300 leading-relaxed font-light whitespace-pre-line">
                    {agent.bio}
                  </p>
                )}

                {/* Firma editorial */}
                <div className="mt-10 pt-8 border-t border-slate-800/60">
                  <p className="font-serif text-lg text-amber-300 italic">
                    {identity.personal.displayName}
                  </p>
                  {identity.corporate.show && (
                    <p className="text-xs tracking-[0.22em] uppercase text-slate-500 mt-2">
                      {identity.corporate.name}
                    </p>
                  )}
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      )}

      {/* ══ ESPECIALIDADES ══ */}
      {agent.specialties && agent.specialties.length > 0 && (
        <section className="relative py-24 md:py-32 bg-slate-900">
          <div className="container mx-auto px-5 sm:px-8 max-w-6xl">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="max-w-2xl mb-16"
            >
              <div className="flex items-center gap-3 mb-6">
                <span className="h-px w-10 bg-amber-400/60" aria-hidden="true" />
                <span className="text-[11px] tracking-[0.32em] uppercase text-amber-300/90">
                  {content.specialties_kicker}
                </span>
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-light text-white leading-tight">
                {content.specialties_heading}
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-800/40">
              {agent.specialties.map((specialty, i) => {
                const Icon = specialtyIcon(specialty)
                return (
                  <motion.div
                    key={specialty}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: Math.min(i * 0.08, 0.4) }}
                    className="bg-slate-900 p-8 md:p-10 flex items-start gap-5 hover:bg-slate-900/60 transition-colors duration-500 group"
                  >
                    <Icon size={22} className="text-amber-400 shrink-0 mt-1 group-hover:scale-110 transition-transform duration-500" aria-hidden="true" />
                    <div>
                      <p className="font-serif text-lg md:text-xl text-white font-light capitalize leading-snug">
                        {specialty}
                      </p>
                    </div>
                  </motion.div>
                )
              })}
            </div>
          </div>
        </section>
      )}

      {/* ══ ZONAS ══ */}
      {agent.zones && agent.zones.length > 0 && (
        <section className="relative py-24 md:py-32 bg-slate-950">
          <div className="container mx-auto px-5 sm:px-8 max-w-6xl">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="max-w-2xl mb-16"
            >
              <div className="flex items-center gap-3 mb-6">
                <span className="h-px w-10 bg-amber-400/60" aria-hidden="true" />
                <span className="text-[11px] tracking-[0.32em] uppercase text-amber-300/90">
                  {content.zones_kicker}
                </span>
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-light text-white leading-tight">
                {content.zones_heading}
              </h2>
            </motion.div>

            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-5">
              {agent.zones.map((zone, i) => (
                <motion.li
                  key={zone}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: Math.min(i * 0.05, 0.4) }}
                  className="flex items-center gap-4 py-4 border-b border-slate-800/60"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" aria-hidden="true" />
                  <span className="font-serif text-base md:text-lg text-slate-200 font-light">
                    {zone}
                  </span>
                </motion.li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* ══ CTA FINAL ══ */}
      <section id="contacto" className="relative py-24 md:py-32 bg-gradient-to-br from-slate-900 via-slate-950 to-zinc-950 overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-amber-500/5 rounded-full blur-3xl pointer-events-none" aria-hidden="true" />

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="relative container mx-auto px-5 sm:px-8 max-w-3xl text-center"
        >
          <div className="flex items-center justify-center gap-3 mb-8">
            <span className="h-px w-10 bg-amber-400/60" aria-hidden="true" />
            <span className="text-[11px] tracking-[0.32em] uppercase text-amber-300/90">
              {content.nav_contact}
            </span>
            <span className="h-px w-10 bg-amber-400/60" aria-hidden="true" />
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light text-white leading-tight mb-8">
            {content.cta_final_heading}
          </h2>
          <p className="text-base md:text-lg text-slate-300 font-light leading-relaxed mb-12 max-w-xl mx-auto">
            {content.cta_final_subheading}
          </p>

          {/* CTAs */}
          {contact.hasAnyChannel ? (
            <div className="flex flex-wrap items-center justify-center gap-5 sm:gap-8">
              {contact.showPhone && (
                <div className="inline-flex">
                  <a
                    href={`tel:${contact.phoneDigits}`}
                    className="group inline-flex items-center gap-3 text-sm tracking-[0.22em] uppercase text-amber-300 border-b border-amber-400/60 hover:border-amber-300 pb-2 transition-colors duration-500"
                  >
                    <Phone size={16} aria-hidden="true" />
                    <span>{content.hero_cta_call}</span>
                  </a>
                </div>
              )}
              {contact.showWhatsapp && (
                <div className="inline-flex">
                  <a
                    href={`https://wa.me/${contact.whatsappDigits}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group inline-flex items-center gap-3 text-sm tracking-[0.22em] uppercase text-amber-300 border-b border-amber-400/60 hover:border-amber-300 pb-2 transition-colors duration-500"
                  >
                    <WhatsAppIcon className="w-4 h-4" />
                    <span>{content.hero_cta_whatsapp}</span>
                  </a>
                </div>
              )}
              {contact.showEmail && (
                <div className="inline-flex">
                  <a
                    href={`mailto:${contact.email}`}
                    className="group inline-flex items-center gap-3 text-sm tracking-[0.22em] uppercase text-slate-200 border-b border-slate-500/60 hover:border-amber-300 hover:text-amber-300 pb-2 transition-colors duration-500"
                  >
                    <Mail size={16} aria-hidden="true" />
                    <span>{content.hero_cta_email}</span>
                  </a>
                </div>
              )}
            </div>
          ) : (
            <p className="text-base md:text-lg text-slate-300 font-light italic max-w-xl mx-auto">
              {chat.showIA
                ? content.hero_chat_fallback
                : content.hero_chat_fallback_no_ia}
            </p>
          )}
        </motion.div>
      </section>

      {/* ══ FOOTER ══ */}
      <footer className="bg-slate-950 border-t border-slate-800/60 pt-20 pb-10">
        <div className="container mx-auto px-5 sm:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">

            {/* Columna 1: Marca corporativa */}
            <div>
              <div className="mb-6">
                {images.corporate.show && images.corporate.url ? (
                  <img
                    src={images.corporate.url}
                    alt={identity.corporate.name || brandForCopyright}
                    className="h-10 w-auto object-contain"
                  />
                ) : (
                  <span className="font-serif text-lg tracking-[0.18em] uppercase text-amber-200">
                    {brandForCopyright}
                  </span>
                )}
              </div>
              {identity.corporate.show && identity.corporate.name && images.corporate.url && (
                <p className="font-serif text-base text-slate-300 mb-4">
                  {identity.corporate.name}
                </p>
              )}
              {contact.showAddress && (
                <div className="mb-3">
                  <p className="text-[10px] tracking-[0.24em] uppercase text-amber-300/70 mb-2">
                    {content.footer_heading_address}
                  </p>
                  <p className="text-sm text-slate-400 leading-relaxed flex items-start gap-2">
                    <MapPin size={14} className="mt-0.5 shrink-0" aria-hidden="true" />
                    <span>{contact.address}</span>
                  </p>
                </div>
              )}
            </div>

            {/* Columna 2: Contacto */}
            <div>
              <p className="text-[10px] tracking-[0.24em] uppercase text-amber-300/70 mb-5">
                {content.footer_heading_contact}
              </p>
              <ul className="space-y-4 text-sm text-slate-300">
                {contact.showPhone && (
                  <li className="flex items-start gap-2.5">
                    <Phone size={14} className="mt-1 shrink-0 text-amber-400/70" aria-hidden="true" />
                    <a href={`tel:${contact.phoneDigits}`} className="hover:text-amber-300 transition-colors break-all">
                      {contact.phoneNumber}
                    </a>
                  </li>
                )}
                {contact.showWhatsapp && (
                  <li className="flex items-start gap-2.5">
                    <WhatsAppIcon className="w-3.5 h-3.5 mt-1 shrink-0 text-amber-400/70" />
                    <a href={`https://wa.me/${contact.whatsappDigits}`} target="_blank" rel="noopener noreferrer" className="hover:text-amber-300 transition-colors break-all">
                      {contact.whatsappNumber}
                    </a>
                  </li>
                )}
                {contact.showEmail && (
                  <li className="flex items-start gap-2.5">
                    <Mail size={14} className="mt-1 shrink-0 text-amber-400/70" aria-hidden="true" />
                    <a href={`mailto:${contact.email}`} className="hover:text-amber-300 transition-colors break-all">
                      {contact.email}
                    </a>
                  </li>
                )}
                {!contact.showPhone && !contact.showWhatsapp && !contact.showEmail && (
                  <li className="text-slate-500 italic text-xs">{content.na}</li>
                )}
              </ul>
            </div>

            {/* Columna 3: Horarios y quejas */}
            <div>
              {contact.showAttentionHours && contact.attentionHours && (
                <div className="mb-6">
                  <p className="text-[10px] tracking-[0.24em] uppercase text-amber-300/70 mb-3 flex items-center gap-2">
                    <Clock size={12} aria-hidden="true" />
                    <span>{content.footer_heading_hours}</span>
                  </p>
                  <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-line">
                    {contact.attentionHours}
                  </p>
                </div>
              )}
              {contact.showComplaintsContact && contact.complaintsContact && (
                <div>
                  <p className="text-[10px] tracking-[0.24em] uppercase text-amber-300/70 mb-3 flex items-center gap-2">
                    <Shield size={12} aria-hidden="true" />
                    <span>{content.footer_heading_complaints}</span>
                  </p>
                  <p className="text-sm text-slate-300 leading-relaxed break-words">
                    {contact.complaintsContact}
                  </p>
                </div>
              )}
            </div>

            {/* Columna 4: Legal + Redes */}
            <div>
              {legal.hasAnyDoc && (
                <div className="mb-8">
                  <p className="text-[10px] tracking-[0.24em] uppercase text-amber-300/70 mb-4">
                    {content.footer_heading_legal}
                  </p>
                  <ul className="space-y-3 text-sm text-slate-300">
                    {legal.availableDocs.map((docType) => (
                      <li key={docType}>
                        <button
                          type="button"
                          onClick={() => openLegalDoc(docType)}
                          className="text-left hover:text-amber-300 transition-colors"
                        >
                          {legalDocLabel(docType)}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {(social.facebook || social.instagram || social.linkedin || social.tiktok || social.youtube) && (
                <div>
                  <p className="text-[10px] tracking-[0.24em] uppercase text-amber-300/70 mb-4">
                    {content.footer_heading_social}
                  </p>
                  <ul className="flex items-center gap-3 flex-wrap">
                    {social.facebook && (
                      <li>
                        <a
                          href={social.facebook}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="Facebook"
                          className="w-9 h-9 flex items-center justify-center border border-slate-700 hover:border-amber-400 hover:text-amber-300 text-slate-300 transition-colors"
                        >
                          <Facebook size={14} aria-hidden="true" />
                        </a>
                      </li>
                    )}
                    {social.instagram && (
                      <li>
                        <a
                          href={social.instagram}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="Instagram"
                          className="w-9 h-9 flex items-center justify-center border border-slate-700 hover:border-amber-400 hover:text-amber-300 text-slate-300 transition-colors"
                        >
                          <Instagram size={14} aria-hidden="true" />
                        </a>
                      </li>
                    )}
                    {social.linkedin && (
                      <li>
                        <a
                          href={social.linkedin}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="LinkedIn"
                          className="w-9 h-9 flex items-center justify-center border border-slate-700 hover:border-amber-400 hover:text-amber-300 text-slate-300 transition-colors"
                        >
                          <Linkedin size={14} aria-hidden="true" />
                        </a>
                      </li>
                    )}
                    {social.tiktok && (
                      <li>
                        <a
                          href={social.tiktok}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="TikTok"
                          className="w-9 h-9 flex items-center justify-center border border-slate-700 hover:border-amber-400 hover:text-amber-300 text-slate-300 transition-colors"
                        >
                          <TikTokIcon className="w-3.5 h-3.5" />
                        </a>
                      </li>
                    )}
                    {social.youtube && (
                      <li>
                        <a
                          href={social.youtube}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="YouTube"
                          className="w-9 h-9 flex items-center justify-center border border-slate-700 hover:border-amber-400 hover:text-amber-300 text-slate-300 transition-colors"
                        >
                          <Youtube size={14} aria-hidden="true" />
                        </a>
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* Línea inferior */}
          <div className="border-t border-slate-800/60 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-500">
            <p>{content.footer_copyright(brandForCopyright)}</p>
          </div>
        </div>
      </footer>

      {/* ══ CHAT WIDGET ══ */}
      <ChatWidget
        agentName={brandForCopyright}
        agentSubdomain={getSubdomain()}
        agentAvatar={images.personal.url ?? undefined}
        agentPlan={agent.plan}
      />

      {/* ══ MODAL LEGAL ══ */}
      {legalModal.open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={content.legal_modal_title}
          className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={closeLegalModal}
        >
          <div
            className="bg-white text-slate-900 rounded-sm max-w-2xl w-full max-h-[85vh] flex flex-col shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center px-6 py-4 border-b border-slate-200">
              <span className="font-serif text-sm tracking-[0.18em] uppercase">
                {legalModal.type
                  ? legalDocLabel(legalModal.type)
                  : content.legal_modal_title}
              </span>
              <button
                type="button"
                onClick={closeLegalModal}
                aria-label={content.legal_modal_close}
                className="text-2xl leading-none text-slate-500 hover:text-slate-900 transition-colors"
            >
                ×
              </button>
            </div>
            <div
              className="overflow-y-auto px-6 py-6 text-sm leading-relaxed"
              dangerouslySetInnerHTML={{ __html: legalModal.html }}
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default ElegantTemplate
